"use client";
import React, { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "@mui/material/styles";
import { useSession } from "next-auth/react";

import SpaceDashboard from "@mui/icons-material/SpaceDashboard";
import PersonSearchRounded from "@mui/icons-material/PersonSearchRounded";
import Insights from "@mui/icons-material/Insights";
import Camera from "@mui/icons-material/Camera";
import BubbleChart from "@mui/icons-material/BubbleChart";
import TocRounded from "@mui/icons-material/TocRounded";
import AppsIcon from "@mui/icons-material/Apps";
import FavoriteRounded from "@mui/icons-material/FavoriteRounded";
import CloseIcon from "@mui/icons-material/Close";
import { VERSION, BUILD_DATE } from "../../../../../../config/version";
import { motion } from "framer-motion";

import AdminUserButton from "@/app/components/admin/AdminUserButton";
import AdminMeetingButton from "@/app/components/admin/AdminMeetingButton";
import AdminModerationButton from "@/app/components/admin/AdminModerationButton";
import AdminServiceButton from "@/app/components/admin/AdminServiceButton";
import AdminAnalyticsButton from "@/app/components/admin/AdminAnalyticsButton";

import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  SwipeableDrawer,
  Divider,
  CircularProgress,
} from "@mui/material";
import Link from "next/link";

const MobileSidebar = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const pathname = usePathname();
  const theme = useTheme();
  const router = useRouter();
  const [loadingItem, setLoadingItem] = useState(null);
  const [navigationProgress, setNavigationProgress] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [targetPath, setTargetPath] = useState(null);

  const { data: session } = useSession();
  const hasAdminPrivileges = session?.user?.tier?.admin_tier;

  useEffect(() => {
    if (isNavigating && targetPath && pathname === targetPath) {
      setNavigationProgress(100);

      setTimeout(() => {
        setLoadingItem(null);
        setNavigationProgress(0);
        setIsNavigating(false);
        setTargetPath(null);
        handleCloseDrawer();
      }, 300);
    }
  }, [pathname, isNavigating, targetPath]);

  const handleCloseDrawer = () => {
    setShowDrawer(false);
  };

  const handleNavigation = async (itemPath, isVideoLink = false) => {
    const loadingPath = itemPath;

    setLoadingItem(loadingPath);
    setIsNavigating(true);
    setTargetPath(isVideoLink ? "/video" : itemPath);

    await new Promise((resolve) => setTimeout(resolve, 50));

    setNavigationProgress(0);

    const progressInterval = setInterval(
      () => {
        setNavigationProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }

          let increment;
          if (isVideoLink) {
            if (prev < 20) {
              increment = 15;
            } else if (prev < 50) {
              increment = 8;
            } else if (prev < 80) {
              increment = 5;
            } else {
              increment = 2;
            }
          } else {
            if (prev < 30) {
              increment = 25;
            } else if (prev < 60) {
              increment = 10;
            } else if (prev < 85) {
              increment = 6;
            } else {
              increment = 3;
            }
          }

          return prev + increment;
        });
      },
      isVideoLink ? 100 : 75,
    );

    try {
      const navigationPromise = isVideoLink
        ? router.push("/video")
        : router.push(itemPath);

      const minLoadingTime = isVideoLink
        ? new Promise((resolve) => setTimeout(resolve, 800))
        : Promise.resolve();

      await Promise.all([navigationPromise, minLoadingTime]);
    } catch (error) {
      clearInterval(progressInterval);
      setLoadingItem(null);
      setNavigationProgress(0);
      setIsNavigating(false);
      setTargetPath(null);
      console.error("Navigation error:", error);
    }
  };

  const menuGroups = [
    {
      title: "Main",
      items: [
        {
          text: "Dashboard",
          icon: <SpaceDashboard sx={{ transform: "rotate(-90deg)" }} />,
          path: "/",
        },
        {
          text: "Get the Meeting",
          icon: <PersonSearchRounded />,
          path: "/search",
        },
        {
          text: "Meeting Requests",
          icon: <TocRounded sx={{ transform: "rotate(180deg)" }} />,
          path: "/meetings",
        },
        {
          text: "Meeting Room",
          icon: <Camera />,
          path: "/video",
          isVideoLink: true,
        },
      ],
    },
    {
      title: "Help & Support",
      items: [
        { text: "Saved Profile", icon: <FavoriteRounded />, path: "/saved" },
        { text: "Performance", icon: <Insights />, path: "/stats" },
        { text: "Help Center", icon: <BubbleChart />, path: "/support" },
      ],
    },
  ];

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          px: 0,
          padding: "16px 4px 16px 24px",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: theme.palette.text.primary,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Meex Hub
        </Typography>
        <IconButton
          onClick={handleCloseDrawer}
          color="inherit"
          size="large"
          disabled={isNavigating}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {hasAdminPrivileges && !session?.impersonating && (
        <>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 1,
                py: 1,
              }}
            >
              <AdminUserButton />
              <AdminMeetingButton />
              <AdminModerationButton />
              <AdminServiceButton />
              <AdminAnalyticsButton />
            </Box>
          </Box>
          <Divider sx={{ mx: 2, opacity: 0.6 }} />
        </>
      )}

      <Box sx={{ flexGrow: 1, px: 2, py: 1, width: "100%", overflowY: "auto" }}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={group.title}>
            <List
              sx={{
                width: "100%",
                padding: 0,
                mb: 2,
                "& .MuiListItemButton-root": {
                  my: 0.75,
                  borderRadius: "12px!important",
                  width: "100%",
                },
              }}
            >
              {group.items.map((item) => {
                const isActive = pathname === item.path;
                const isLoading = loadingItem === item.path;

                return (
                  <div key={item.path}>
                    <ListItemButton
                      key={item.path}
                      component={
                        item.customHandler || item.isVideoLink || isLoading
                          ? "div"
                          : Link
                      }
                      href={
                        item.customHandler || isLoading ? undefined : item.path
                      }
                      onClick={
                        isLoading
                          ? undefined
                          : item.customHandler ||
                            (item.isVideoLink
                              ? async () => {
                                  await handleNavigation(item.path, true);
                                }
                              : async () => {
                                  await handleNavigation(item.path, false);
                                })
                      }
                      sx={{
                        my: 0.75,
                        borderRadius: "12px",
                        width: "100%",
                        position: "relative",
                        overflow: "hidden",
                        minHeight: "56px",
                        alignItems: "center",

                        backgroundColor: isActive
                          ? theme.palette.grey[400]
                          : "transparent",

                        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",

                        ...(isActive &&
                          !isLoading && {
                            backgroundColor: theme.palette.grey[400],
                            color: theme.palette.common.white,
                            "& .MuiListItemText-primary": {
                              color: theme.palette.common.white,
                              fontWeight: 700,
                            },
                          }),

                        "&:hover": {
                          backgroundColor: isLoading
                            ? "transparent"
                            : isActive
                              ? theme.palette.grey[400]
                              : theme.palette.grey[50],
                        },
                      }}
                    >
                      {isLoading && (
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: `${navigationProgress}%` }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            height: "100%",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[300],
                            borderRadius: "12px !important",
                            zIndex: 0,
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          mr: 2.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                          zIndex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            color: isActive
                              ? theme.palette.common.white
                              : theme.palette.grey[400],
                            transition: "color 0.15s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {item.icon}
                        </Box>
                      </Box>

                      <ListItemText
                        sx={{
                          zIndex: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                        primary={
                          <Typography
                            sx={{
                              fontSize: "1.1rem",
                              fontWeight: isLoading ? 700 : 600,

                              color: isActive
                                ? theme.palette.common.white
                                : "text.primary",

                              transition: "all 0.15s ease",
                              lineHeight: "1.2",
                            }}
                          >
                            {item.text}
                          </Typography>
                        }
                      />

                      {isLoading && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            zIndex: 1,
                            ml: 1,
                          }}
                        >
                          <CircularProgress
                            size={16}
                            sx={{
                              color: "white",
                            }}
                          />
                        </Box>
                      )}
                    </ListItemButton>
                  </div>
                );
              })}
            </List>

            {groupIndex !== menuGroups.length - 1 && (
              <Divider sx={{ my: 1.5, opacity: 0.6 }} />
            )}
          </React.Fragment>
        ))}
      </Box>

      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: "auto",
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          color={theme.palette.text.secondary}
          align="center"
        >
          © {new Date().getFullYear()} Meex, Inc.
        </Typography>
        <Typography variant="caption" color={theme.palette.text.secondary}>
          Version {VERSION}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      <motion.div whileTap={{ scale: 0.95 }}>
        <IconButton
          size="large"
          color="inherit"
          onClick={() => setShowDrawer(true)}
          sx={{
            ...(showDrawer && {
              color: "primary.main",
            }),
          }}
        >
          <AppsIcon fontSize="large" />
        </IconButton>
      </motion.div>
      <SwipeableDrawer
        anchor="right"
        open={showDrawer}
        onClose={() => !isNavigating && setShowDrawer(false)}
        onOpen={() => setShowDrawer(true)}
        disableSwipeToOpen={isNavigating}
        transitionDuration={{
          enter: 350,
          exit: 250,
        }}
        slotProps={{
          backdrop: {
            style: {
              transition: "opacity 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              backdropFilter: "blur(8px)",
            },
          },
          paper: {
            sx: {
              width: { xs: "85%", sm: "50%" },
              // height: "100vh",
              backgroundColor: "background.paper",
              border: "none",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
              overflowX: "hidden",
              position: "fixed",
              top: 0,
              right: 0,
              zIndex: 9999,
              transform: showDrawer ? "translateX(0)" : "translateX(100%)",
              transition:
                "transform 350ms cubic-bezier(0.4, 0.0, 0.2, 1) !important",
              "&.MuiDrawer-paperAnchorRight": {
                transition:
                  "transform 350ms cubic-bezier(0.4, 0.0, 0.2, 1) !important",
              },
            },
          },
        }}
        sx={{
          "& .MuiDrawer-paper": {
            overflowX: "hidden",
            height: "100vh",
            transform: "translateX(100%)",
            transition:
              "transform 350ms cubic-bezier(0.4, 0.0, 0.2, 1) !important",
          },
          "& .MuiPaper-root": {
            width: "85%",
          },
          "& .MuiBackdrop-root": {
            transition:
              "opacity 350ms cubic-bezier(0.4, 0.0, 0.2, 1) !important",
          },
          zIndex: 9999,
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    </Box>
  );
};

export default MobileSidebar;
